import { EntityResolved } from '@api';
import { Icon } from '@design/components/icon/Icon';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { TableCell, TableHeader } from '@design/components/table';
import { TanStackTableRoot } from '@design/components/tanstack-table/TanStackTable';
import { DragDropProvider } from '@dnd-kit/react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { RowSelectionCheckbox } from '@terminal/components/row-selection-checkbox/RowSelectionCheckbox';
import { UseRowsSelectionReturnType } from '@terminal/hooks/use-rows-selection/useRowsSelection';
import { useOnboardingStore } from '@terminal/views/onboarding/hooks/useOnboardingStore';
import clsx from 'clsx';
import { RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DraggableWatchlistRow } from '../draggable-watchlist-row/DraggableWatchlistRow';
import styles from './TableWatchlistManager.module.scss';
import { EntityCellBaseHandlers } from './table-cells/entity-cell/EntityCell';
import { getColumns, INPUT_HEADER_COLUMN_ID } from './utils';

export interface TableWatchlistManagerWidgetProps
  extends Omit<UseRowsSelectionReturnType, 'setRowSelection'> {
  data: EntityResolved[];
  countEntitiesAdded: number;
  containerRef: RefObject<HTMLDivElement | null>;
  unMappedRowRef?: RefObject<HTMLTableRowElement | null>;
  isEmptyListError?: boolean;
  handlers: EntityCellBaseHandlers;
  isPending?: boolean;
  hasNextPage?: boolean;
  isLoadingEntities?: boolean;
}

export const TableWatchlistManagerWidget = ({
  data,
  countEntitiesAdded,
  toggleAllRowsSelection,
  rowSelection,
  updateRowSelection,
  unMappedRowRef,
  containerRef,
  isEmptyListError,
  handlers,
  isPending = false,
  hasNextPage = false,
  isLoadingEntities = false,
}: TableWatchlistManagerWidgetProps) => {
  const { t } = useTranslation(['watchlistManager', 'analyze']);
  const { unMappedEntitiesList, entitiesList } = useOnboardingStore(state => ({
    unMappedEntitiesList: state.unMappedEntitiesList,
    entitiesList: state.entitiesList,
  }));

  const includeInput =
    !!unMappedEntitiesList.length || entitiesList.some(e => e.input);
  const optionalColumns = {
    inputColumn: includeInput,
  };
  const columns = useMemo(
    () =>
      getColumns({
        t,
        toggleAllRowsSelection,
        optionalColumns,
        handlers,
      }),
    [includeInput]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.tag ?? row.input,
  });
  const tableRows = table.getRowModel().rows;

  const unMappedTable = useReactTable({
    data: unMappedEntitiesList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: data => data.input,
  });
  const unMappedTableRows = unMappedTable.getRowModel().rows;

  const hasData = !!data.length || !!unMappedEntitiesList.length;
  const showPaginationLoader = hasNextPage || isLoadingEntities;
  const showTableHeader = hasData || isLoadingEntities;
  return (
    <DragDropProvider>
      <div className={styles.container} ref={containerRef}>
        <TanStackTableRoot
          className={styles.styledTanStackTableRoot}
          data-twm-table
        >
          <thead>
            {showTableHeader &&
              table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableHeader
                      alignment="left"
                      key={header.id}
                      className={clsx(
                        header.column.id === INPUT_HEADER_COLUMN_ID
                          ? styles.styledTableHeaderSmall
                          : styles.styledTableHeader,
                        {
                          [styles.styledTableHeaderError]: isEmptyListError,
                        }
                      )}
                      data-twm-header
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHeader>
                  ))}
                </tr>
              ))}
          </thead>
          <tbody>
            {isPending && (
              <tr>
                <TableCell colspan={table.getHeaderGroups()[0].headers.length}>
                  <SkeletonLoader
                    areas={[
                      {
                        styles: {
                          width: '100%',
                          height: '120px',
                          borderRadius: '8px',
                        },
                      },
                    ]}
                  />
                </TableCell>
              </tr>
            )}
            {!!data.length && (
              <>
                <tr className={styles.styledFullRow}>
                  <td
                    colSpan={table.getHeaderGroups()[0].headers.length}
                    id="confirm-assets-table-header"
                    data-testid="confirm-assets-table-header"
                    className={styles.styledHeaderCell}
                  >
                    <span className={styles.headerCellLabel}>
                      {t('watchlistManager:confirmMapping')}
                    </span>
                  </td>
                </tr>
                {tableRows.map((row, index) => {
                  const isNew = index < countEntitiesAdded;
                  return (
                    <DraggableWatchlistRow
                      data-testid="created-watchlist-row"
                      key={row.id}
                      row={row}
                      afterNode={
                        <RowSelectionCheckbox
                          name={`watchlist-${row.id}`}
                          checked={row.getIsSelected()}
                          onChange={() =>
                            updateRowSelection(row.id, !row.getIsSelected())
                          }
                          variant={isEmptyListError ? 'critical' : 'primary'}
                        />
                      }
                    >
                      {row.getVisibleCells().map(cell => {
                        return (
                          <TableCell
                            key={cell.id}
                            alignment="left"
                            className={
                              isNew
                                ? styles.styledTableCellNew
                                : styles.styledTableCell
                            }
                            isNumber={false}
                            data-twm-cell
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </DraggableWatchlistRow>
                  );
                })}
              </>
            )}
            {!!unMappedEntitiesList.length && (
              <tr className={styles.styledFullRow} ref={unMappedRowRef}>
                <td
                  colSpan={table.getHeaderGroups()[0].headers.length}
                  id="unmapped-table-header"
                  data-testid="unmapped-table-header"
                  className={styles.styledHeaderCell}
                >
                  <span className={styles.headerCellLabel}>
                    {t('watchlistManager:unmappedAssets')}
                  </span>
                </td>
              </tr>
            )}
            {unMappedTableRows.map(row => (
              <DraggableWatchlistRow
                key={row.id}
                row={row}
                afterNode={
                  <Icon
                    className={styles.styledIcon}
                    iconName="QuestionMarkCircle"
                    fillColor="var(--icon-critical)"
                    size={20}
                  />
                }
              >
                {row.getVisibleCells().map(cell => {
                  return (
                    <TableCell
                      key={cell.id}
                      alignment="left"
                      className={styles.styledTableCell}
                      isNumber={false}
                      data-twm-cell
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </DraggableWatchlistRow>
            ))}
            {showPaginationLoader && (
              <tr data-testid="pagination-loader">
                <TableCell colspan={table.getHeaderGroups()[0].headers.length}>
                  <SkeletonLoader
                    areas={[
                      {
                        styles: {
                          height: '24px',
                          width: '100%',
                          borderRadius: '8px',
                        },
                      },
                    ]}
                  />
                </TableCell>
              </tr>
            )}
          </tbody>
        </TanStackTableRoot>
      </div>
    </DragDropProvider>
  );
};
